import React from 'react'
import styled from 'styled-components'

export type BadgeType = 'info' | 'success' | 'error' | 'warning' | 'default' | 'magic'

interface Props extends React.DetailedHTMLProps<any, HTMLDivElement> {
  type?: BadgeType
  soft?: boolean
  children: React.ReactNode
}

export const Badge: React.FC<Props> = ({
  type = 'info',
  soft = false,
  children,
  className,
  ...rest
}) => {
  const mergedClassName = [type, soft ? 'soft' : null, className].filter(Boolean).join(' ')
  return (
    <StyledBadge className={mergedClassName} {...rest}>
      <BadgeContent>{children}</BadgeContent>
    </StyledBadge>
  )
}

const StyledBadge = styled.div`
  display: inline-block;
  border-radius: ${({ theme }) => theme.borderRadius.lg}rem;
  background-color: transparent;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  gap: ${({ theme }) => theme.spacing.xxs}rem;

  svg {
    width: ${({ theme }) => theme.fontSize.xs}rem;
    height: ${({ theme }) => theme.fontSize.xs}rem;
  }

  &.info {
    border: 1px solid ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  &.success {
    border: 1px solid ${({ theme }) => theme.colors.success};
    color: ${({ theme }) => theme.colors.success};
  }

  &.error {
    border: 1px solid ${({ theme }) => theme.colors.error};
    color: ${({ theme }) => theme.colors.error};
  }

  &.warning {
    border: 1px solid ${({ theme }) => theme.colors.warning};
    color: ${({ theme }) => theme.colors.warning};
  }

  &.default {
    border: 1px solid ${({ theme }) => theme.colors.neutralGray};
    color: ${({ theme }) => theme.colors.metalGray};
  }

  &.magic {
    border: 1px solid ${({ theme }) => theme.colors.AIPurple};
    color: ${({ theme }) => theme.colors.AIPurple};
  }

  &.soft {
    border: none;

    &.info {
      background-color: color-mix(in srgb, ${({ theme }) => theme.colors.primary} 12%, white);
      color: ${({ theme }) => theme.colors.primary};
    }

    &.success {
      background-color: color-mix(in srgb, ${({ theme }) => theme.colors.success} 14%, white);
      color: ${({ theme }) => theme.colors.success};
    }

    &.error {
      background-color: color-mix(in srgb, ${({ theme }) => theme.colors.error} 12%, white);
      color: ${({ theme }) => theme.colors.error};
    }

    &.warning {
      background-color: color-mix(in srgb, ${({ theme }) => theme.colors.warning} 16%, white);
      color: ${({ theme }) => theme.colors.warning};
    }

    &.default {
      background-color: ${({ theme }) => theme.colors.neutralGray};
      color: ${({ theme }) => theme.colors.metalGray};
    }

    &.magic {
      background-color: color-mix(in srgb, ${({ theme }) => theme.colors.AIPurple} 12%, white);
      color: ${({ theme }) => theme.colors.AIPurple};
    }
  }
`

const BadgeContent = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxs}rem ${({ theme }) => theme.spacing.xs}rem;
`
